import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { env } from "@/src/common/constants/env";
import { SITE_ORIGIN } from "@/src/common/constants/site-config";
import { DEFAULT_LANGUAGE, LANGUAGE_QUERY_PARAM, LANGUAGES, OG_LOCALE, toLanguage } from "@/src/common/locales/i18n";

interface PageHelmetProps {
	title: string;
	description?: string;
	ogImage?: string | null;
	noindex?: boolean;
}

const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

const buildAlternateUrl = (pathname: string, language: string) =>
	language === DEFAULT_LANGUAGE ? `${SITE_ORIGIN}${pathname}` : `${SITE_ORIGIN}${pathname}?${LANGUAGE_QUERY_PARAM}=${language}`;

export function PageHelmet({ title, description, ogImage, noindex = false }: PageHelmetProps) {
	const { pathname } = useLocation();
	const { i18n } = useTranslation();
	const language = toLanguage(i18n.resolvedLanguage);
	const canonicalUrl = buildAlternateUrl(pathname, language);
	const image = ogImage ?? DEFAULT_OG_IMAGE;
	const titleTemplate = title.includes(env.APP_NAME) ? "%s" : `%s | ${env.APP_NAME}`;

	return (
		<Helmet defaultTitle={env.APP_NAME} titleTemplate={titleTemplate} title={title} htmlAttributes={{ lang: language }}>
			{noindex ? <meta name="robots" content="noindex" /> : <link rel="canonical" href={canonicalUrl} />}
			{noindex
				? null
				: Object.keys(LANGUAGES).map((code) => (
						<link key={code} rel="alternate" hrefLang={code} href={buildAlternateUrl(pathname, code)} />
					))}
			{noindex ? null : <link rel="alternate" hrefLang="x-default" href={buildAlternateUrl(pathname, DEFAULT_LANGUAGE)} />}
			{description && <meta name="description" content={description} />}
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={env.APP_NAME} />
			<meta property="og:locale" content={OG_LOCALE[language]} />
			<meta property="og:url" content={canonicalUrl} />
			<meta property="og:title" content={title} />
			{description && <meta property="og:description" content={description} />}
			<meta property="og:image" content={image} />
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={title} />
			{description && <meta name="twitter:description" content={description} />}
			<meta name="twitter:image" content={image} />
		</Helmet>
	);
}
